import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { IC_CalculationTrace, IC_ExecutionDetail, IC_RecalculationPreview } from 'src/app/Interface/IC_Interface';
import { ICService } from 'src/app/Service/IC/IC.service';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { icScientificTitle, icScientificNotice } from '../ic-scientific-presentation';
import { ICUncertaintyBudgetPreview } from '../ic-uncertainty-budget';

@Component({
  selector: 'app-ic-calculation-details',
  templateUrl: './ICCalculationDetails.component.html',
  styleUrls: ['./ICCalculationDetails.component.css']
})
export class ICCalculationDetailsComponent implements OnChanges, OnDestroy {
  @Input() uncertaintyPreview?: ICUncertaintyBudgetPreview;
  standardBudgetU(id: string): string { return this.uncertaintyPreview?.evaluation.trace.steps.find(s => s.code === `${id}.StandardU`)?.result ?? 'قابل محاسبه نیست'; }
  readonly scientificTitle = icScientificTitle;
  readonly scientificNotice = icScientificNotice;
  @Input() detail?: IC_ExecutionDetail;
  @Input() loading = false;
  @Input() error = '';
  @Input() previewMode = false;
  exporting: 'pdf' | 'excel' | 'review-pdf' | 'review-excel' | null = null;
  exportError = '';
  recalculating = false;
  reviewError = '';
  reviewMissingInputs: string[] = [];
  preview?: IC_RecalculationPreview;
  private reviewRequest?: Subscription;
  constructor(private service: ICService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.detail) {
      this.reviewRequest?.unsubscribe(); this.preview = undefined; this.reviewError = '';
      this.reviewMissingInputs = []; this.exportError = '';
    }
  }
  ngOnDestroy(): void { this.reviewRequest?.unsubscribe(); }

  get canExport(): boolean {
    return !this.previewMode && !!this.detail?.results?.length && this.detail.results.every(result => !!result.calculationTrace);
  }

  recalculate(): void {
    if (this.previewMode || this.recalculating || this.exporting || !this.detail?.legacyReview?.canRecalculate || this.loading || this.error) return;
    const id = this.detail.execution.id;
    this.recalculating = true; this.reviewError = ''; this.reviewMissingInputs = []; this.preview = undefined;
    this.reviewRequest = this.service.recalculatePreview(id).pipe(finalize(() => this.recalculating = false)).subscribe({
      next: preview => { if (this.detail?.execution.id === id) this.preview = preview; },
      error: error => {
        this.reviewMissingInputs = error?.error?.missingInputs || [];
        this.reviewError = this.reviewMissingInputs.length ? 'داده‌های لازم برای بازمحاسبه کامل نیست.' :
          error?.error?.message || 'دریافت بازمحاسبه جهت بررسی ناموفق بود.';
      }
    });
  }

  get previewDiffers(): boolean { return this.preview?.matches === false || !!this.preview?.itemComparisons?.some(item => item.matches === false); }
  recordedForItem(id: number) { return this.detail?.legacyReview?.items.find(item => item.itemId === id); }
  recordedValue(value: unknown): string { return value === null || value === undefined || value === '' ? 'ثبت نشده / قابل بازیابی نیست' : String(value); }
  comparison(value: boolean | null): string { return value == null ? 'قابل مقایسه نیست' : value ? 'یکسان' : 'متفاوت'; }
  get displayedItems(): { id: number; title: string }[] {
    const items = (this.detail?.items || []).map(item => ({ id: item.id, title: item.title }));
    for (const result of this.detail?.results || []) {
      if (!items.some(item => item.id === result.iC_Template_Item_ID))
        items.push({ id: result.iC_Template_Item_ID, title: `آیتم ثبت‌شده شماره ${result.iC_Template_Item_ID}` });
    }
    return items;
  }

  exportReview(format: 'pdf' | 'excel'): void {
    if (this.exporting || this.recalculating || !this.preview?.canRecalculate || !this.detail || this.previewMode) return;
    const id = this.detail.execution.id;
    this.exporting = format === 'pdf' ? 'review-pdf' : 'review-excel'; this.exportError = '';
    this.service.exportReviewPreview(id, format).pipe(finalize(() => this.exporting = null)).subscribe({
      next: blob => this.downloadFile(blob, `IC-Review-Preview-${id}.${format === 'pdf' ? 'pdf' : 'xlsx'}`),
      error: error => this.handleExportError(error)
    });
  }

  private async handleExportError(error: any): Promise<void> {
    let message = error?.error?.message;
    if (!message && error?.error instanceof Blob) {
      try { const body = JSON.parse(await error.error.text()); message = body.message || body.missingInputs?.join('، '); } catch { }
    }
    this.exportError = message || 'دریافت خروجی ناموفق بود؛ دوباره تلاش کنید.';
  }

  export(format: 'pdf' | 'excel'): void {
    if (this.exporting || this.recalculating || this.loading || this.error || !this.detail || !this.canExport) return;
    const id = this.detail.execution.id;
    this.exporting = format; this.exportError = '';
    const request = format === 'pdf' ? this.service.exportExecutionPdf(id) : this.service.exportExecutionExcel(id);
    request.pipe(finalize(() => this.exporting = null)).subscribe({
      next: blob => this.downloadFile(blob, `IC-Execution-${id}.${format === 'pdf' ? 'pdf' : 'xlsx'}`),
      error: error => this.handleExportError(error)
    });
  }

  downloadFile(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = name; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  ruleClass(passed: boolean | null): string {
    return passed === true ? 'badge-success' : passed === false ? 'badge-danger' : 'badge-secondary';
  }
  traceForItem(id: number): IC_CalculationTrace | undefined | null {
    return this.detail?.results.find(result => result.iC_Template_Item_ID === id)?.calculationTrace;
  }
}
